import { reportingLeLensDatasetExtendCheckPath } from '~/consts/reportingLeLensApi';

import type { LensDatasetExtendCheckResponse } from '~/hooks/apis/reporting/useLeLensDatasetChecks';

import { encodeRequestBody } from '~/utils/encodeRequestBody';
import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

/**
 * The extend-check guard (00_PLAN.md, "Backend endpoints"; F4 in
 * 01_USER_FLOWS.md). Dry runs a dataset's rewritten SQL once, re-shapes every
 * card that reads it against the old and new rows, and reports whether the
 * grain and every dependent card's number survive. Never applies anything on
 * its own; the caller decides what to do with the result.
 *
 * `useExtendCheckReportingLeLensDataset` (useLeLensDatasetChecks.ts) already
 * wraps this same request as a mutation hook for a component to call from an
 * event handler. `buildMissingCards` (PlanGateDialog.tsx) calls it from
 * inside a sequential for-loop instead, which a hook cannot be: this is the
 * same request as a plain async function, built on the same path constant
 * and response type so the two never disagree about the shape.
 *
 * Only callable against a dataset already saved on a real Lens: the route is
 * `:lensId/datasets/:datasetId/extend-check`, so a brand new dashboard with
 * nothing saved yet has nothing for this to check against (PlanGateDialog.tsx
 * skips the call in that case and applies the change optimistically, the same
 * as it always has).
 */

export type DatasetExtendCheckResult = LensDatasetExtendCheckResponse;

export async function runDatasetExtendCheck(
  lensId: string,
  datasetId: string,
  sql: string,
  grainKeys?: ReadonlyArray<string>,
): Promise<DatasetExtendCheckResult> {
  return requestWithTimeout<DatasetExtendCheckResult>(
    resolveReportingLeLensUrl(reportingLeLensDatasetExtendCheckPath(lensId, datasetId)),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(encodeRequestBody({ sql, ...(grainKeys?.length ? { grain: { keys: grainKeys } } : {}) })),
    },
    'Checking this change is taking too long. Please try again.',
  );
}

/** Whether the guard's result means the change is safe to apply without the
 *  reader being shown anything more: the grain held and every dependent card
 *  came back identical. A dataset with no dependent cards yet (added mid the
 *  same build) has nothing to disagree, so an empty `cardDiffs` counts as a
 *  pass. */
export function extendCheckRefused(result: DatasetExtendCheckResult): boolean {
  return !result.grainKept || result.cardDiffs.some((diff) => !diff.identical);
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) {
    return '0 bytes';
  }
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * The pass copy from 02_RIGHT_PANEL_SPEC.md: "+refund_aud NUMERIC. 8 of 8
 * columns kept. Grain unchanged. 4 cards re-run: identical. 14 MB scanned."
 *
 * `originalColumnCount` is the dataset's declared column count before this
 * rewrite, needed to say "X of Y kept"; the guard result alone only carries
 * what changed, not the total it changed against. The type each added
 * column reports isn't in the guard's own response (`added` is names only),
 * so it is left out here rather than guessed.
 */
export function describeExtendCheckPass(result: DatasetExtendCheckResult, originalColumnCount: number): string {
  const addedList = result.added.length ? `+${result.added.join(', +')}. ` : '';
  const kept = originalColumnCount - result.removed.length;
  const cardWord = result.cardDiffs.length === 1 ? 'card' : 'cards';
  const cards = result.cardDiffs.length ? ` ${result.cardDiffs.length} ${cardWord} re-run: identical.` : '';
  return `${addedList}${kept} of ${originalColumnCount} columns kept. Grain unchanged.${cards} ${formatBytes(result.bytesProcessed)} scanned.`;
}

/** The refusal copy from 02_RIGHT_PANEL_SPEC.md: "This would add hotel_type
 *  to the grain. 2 cards would change: ..." Named columns before the guard's
 *  own "grain not kept" fact take precedence, since a dropped column is the
 *  more specific and more actionable half of a grain break. */
export function describeExtendCheckRefusal(result: DatasetExtendCheckResult): string {
  const changed = result.cardDiffs.filter((diff) => !diff.identical);
  const cardWord = changed.length === 1 ? 'card' : 'cards';
  const grainNote = !result.grainKept
    ? 'This would change the dataset’s grain. '
    : result.removed.length
      ? `This would drop ${result.removed.join(', ')}. `
      : '';
  return changed.length
    ? `${grainNote}${changed.length} ${cardWord} would change.`
    : `${grainNote}The grain would no longer be unique on this dataset’s rows.`;
}
